/**
 * Send Email Modal Component
 * Form to send invoice via email
 */

import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { SendInvoiceEmailRequest } from '@/types';
import { Loader2, AlertCircle, Plus, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface SendEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (emailRequest: SendInvoiceEmailRequest) => void;
  isSubmitting?: boolean;
  error?: string | null;
  customerEmail?: string;
  invoiceNumber: string;
}

export const SendEmailModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  error = null,
  customerEmail,
  invoiceNumber,
}: SendEmailModalProps) => {
  const [formData, setFormData] = useState<SendInvoiceEmailRequest>({
    recipientEmail: customerEmail || '',
    ccEmails: [],
    subject: `Invoice ${invoiceNumber}`,
    message: `Dear Customer,\n\nPlease find attached Invoice ${invoiceNumber}.\n\nThank you for your business.`,
    includeAttachment: true,
  });

  const [ccEmailInput, setCcEmailInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddCcEmail = () => {
    if (!ccEmailInput.trim()) return;

    if (!validateEmail(ccEmailInput)) {
      setValidationError('Invalid email address');
      return;
    }

    if (formData.ccEmails?.includes(ccEmailInput)) {
      setValidationError('Email already added to CC list');
      return;
    }

    setFormData({
      ...formData,
      ccEmails: [...(formData.ccEmails || []), ccEmailInput],
    });
    setCcEmailInput('');
    setValidationError(null);
  };

  const handleRemoveCcEmail = (email: string) => {
    setFormData({
      ...formData,
      ccEmails: formData.ccEmails?.filter((e) => e !== email),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.recipientEmail?.trim()) {
      setValidationError('Recipient email is required');
      return;
    }

    if (!validateEmail(formData.recipientEmail)) {
      setValidationError('Invalid recipient email address');
      return;
    }

    setValidationError(null);
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      recipientEmail: customerEmail || '',
      ccEmails: [],
      subject: `Invoice ${invoiceNumber}`,
      message: `Dear Customer,\n\nPlease find attached Invoice ${invoiceNumber}.\n\nThank you for your business.`,
      includeAttachment: true,
    });
    setCcEmailInput('');
    setValidationError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Send Invoice via Email</DialogTitle>
          <DialogDescription>
            Send Invoice {invoiceNumber} to customer or specified recipients
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Error Alert */}
            {(error || validationError) && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error || validationError}</AlertDescription>
              </Alert>
            )}

            {/* Recipient Email */}
            <div className="space-y-2">
              <Label htmlFor="recipientEmail">
                Recipient Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="recipientEmail"
                type="email"
                value={formData.recipientEmail}
                onChange={(e) =>
                  setFormData({ ...formData, recipientEmail: e.target.value })
                }
                placeholder="customer@example.com"
                required
              />
              {customerEmail && (
                <p className="text-sm text-muted-foreground">
                  Customer email: {customerEmail}
                </p>
              )}
            </div>

            {/* CC Emails */}
            <div className="space-y-2">
              <Label htmlFor="ccEmail">CC Recipients (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="ccEmail"
                  type="email"
                  value={ccEmailInput}
                  onChange={(e) => setCcEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCcEmail();
                    }
                  }}
                  placeholder="Add CC email"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleAddCcEmail}
                  disabled={!ccEmailInput.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* CC Email List */}
              {formData.ccEmails && formData.ccEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.ccEmails.map((email) => (
                    <div
                      key={email}
                      className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-sm"
                    >
                      <span>{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveCcEmail(email)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                placeholder="Email subject"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                placeholder="Email message"
                rows={5}
              />
            </div>

            {/* Include Attachment */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeAttachment"
                checked={formData.includeAttachment}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, includeAttachment: checked as boolean })
                }
              />
              <Label
                htmlFor="includeAttachment"
                className="text-sm font-normal cursor-pointer"
              >
                Include invoice PDF as attachment
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Email'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
